/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";

import { api } from "../../../lib/api";

import { useScheduleInfos } from "../../../providers/schedule";

import { IDoctorData } from "../../../providers/doctorsData/types";

import { IDoctorPlaces } from "../../../types/doctor";
import {
  ISearchAvailableTimes,
  IDates,
} from "../../../types/SearchAvalilableTimes";

import { convertNextDates } from "./utils/nextDates";
import { serializeAgenda } from "../../../utils/serializeAgenda";

import { SchedulesSkeleton } from "../../Skeletons/AgendaSkeleton";

import { ConsultationRequest } from "./components/ConsultationRequest";
import { NoAgendaCard } from "./components/NoAgendaCard";
import { PlacesCard } from "./components/PlacesCard";
import { AgendaCard } from "./components/AgendaCard";
import { AgendamentoCard } from "./components/AgendamentoCard";
import { usePartnerData } from "../../../providers/partnerData";
import { useLocationData } from "../../../providers/geolocation";
import { useAbortableRequest } from "../../../hooks/abortableController";
import { useScheduleAvailability } from "../../../providers/scheduleAvailability";

export const ProfessionalCard = (doctor: IDoctorData) => {
  const { image, gender, crm, name, places, id } = doctor;
  const { pathname } = useLocation();
  const { partnerUrl } = usePartnerData();
  const { scheduleInfos } = useScheduleInfos();
  const { createController } = useAbortableRequest();

  const { mapBig } = useLocationData();

  const { getDoctorAvailability, batchLoading, batchAttempted, batchEndDate } =
    useScheduleAvailability();

  const [placePosition, setPlacePosition] = useState<number>(0);
  const [selectedLocal, setSelectedLocal] = useState<IDoctorPlaces>(places[placePosition]);

  const [agenda, setAgenda] = useState<IDates[]>([]);
  const [searchingAgenda, setSearchingAgenda] = useState<boolean>(true);

  const [openAgendamentoModal, setOpenAgendamentoModal] = useState<boolean>(false);
  const [requestConsultModal, setRequestConsultModal] = useState<boolean>(false);

  const [splitedNextDates, setSplitedNextDates] = useState<string[][]>([]);
  const [splitedNextDatesPos, setSplitedNextDatesPos] = useState<number>(0);

  const prevPlacePos = useRef(placePosition);

  const startAgenda = 0;
  const endAgenda = 3;

  const reset = () => {
    setAgenda([]);
    setSearchingAgenda(false);
    return;
  };

  const getAgendas = async (
    startDate: string,
    endDate: string,
    localId: string = String(selectedLocal.id)
  ) => {

    const abortController = createController(`getAgendas${id}`);

    try {
      setSearchingAgenda(true);
      const reqBody = {
        startDate: startDate,
        endDate: endDate,
        profissionaisIds: [id],
        especialidadesIds: [],
        conveniosIds: [],
        keywords: [],
        source: 3,
      };

      const { data } = (await api.post(
        "Schedule/SearchAvaliableTimes",
        reqBody,
        {
          signal: abortController.signal,
        }
      )) as {
        data: ISearchAvailableTimes[];
      };

      const doc = data[0];

      if (!doc) {
        return reset();
      }

      const schedulePlace = doc.places.find(
        (elm) => elm.idLocalEndereco == localId
      );

      if (!schedulePlace) {
        return reset();
      }

      const serializedAgenda = serializeAgenda(schedulePlace.dates);

      if (serializedAgenda.length > 0) {
        setAgenda(serializedAgenda);
      }
      setSearchingAgenda(false);
    } catch (error) {}
  };

  const handleGetNextDates = async () => {
    if (!splitedNextDates.at(splitedNextDatesPos + 1)) {
      return;
    }
    const startHour = splitedNextDates[splitedNextDatesPos + 1].at(0);
    const endHour = splitedNextDates[splitedNextDatesPos + 1].at(-1);

    if (!startHour || !endHour) {
      return reset();
    }

    await getAgendas(startHour.split("T")[0], endHour.split("T")[0]);

    setSplitedNextDatesPos(splitedNextDatesPos + 1);
  };

  const handleGetPreviousDates = async () => {
    if (!splitedNextDates[splitedNextDates.length - 1]) {
      return;
    }

    if (splitedNextDatesPos === 0) {
      return;
    }
    const startHour = splitedNextDates[splitedNextDatesPos - 1].at(0);
    const endHour = splitedNextDates[splitedNextDatesPos - 1].at(-1);

    if (!startHour || !endHour) {
      return reset();
    }

    await getAgendas(startHour.split("T")[0], endHour.split("T")[0]);

    setSplitedNextDatesPos(splitedNextDatesPos - 1);
  };

  const handleOpenAgendamentoModal = () => {
    scheduleInfos.doctor = doctor;
    scheduleInfos.place = selectedLocal;

    setOpenAgendamentoModal(true);
  };

  /**
   * Tenta montar a agenda a partir do lote pré-carregado.
   * Retorna false quando o lote não cobre este médico/local/janela —
   * o chamador cai no fetch individual (comportamento original).
   */
  const trySeedFromBatch = (
    local: IDoctorPlaces,
    chunks: string[][]
  ): boolean => {
    if (!batchEndDate) return false;

    const batchPlaces = getDoctorAvailability(id);
    if (!batchPlaces) return false;

    const place = batchPlaces.find(
      (p) => String(p.idLocalEndereco) === String(local.id)
    );
    if (!place) return false;

    const firstChunk = chunks[0];
    const firstDate = firstChunk.at(0)?.split("T")[0];
    const lastDate = firstChunk.at(-1)?.split("T")[0];

    // janela do card começa além do lote (ex.: próxima data em 3 semanas)
    if (!firstDate || !lastDate || lastDate > batchEndDate) return false;

    const windowDates = place.dates.filter((d) => {
      const day = d.data.split("T")[0];
      return day >= firstDate && day <= lastDate;
    });
    if (windowDates.length === 0) return false;

    const serialized = serializeAgenda(windowDates);
    if (serialized.length === 0) return false;

    setAgenda(serialized);
    setSearchingAgenda(false);
    return true;
  };

  const handleFirstData = async (local: IDoctorPlaces) => {
    try {
      if (!local || !local.nextDates || !local.nextDates.length) {
        return reset();
      }

      const newFormattedNextDates = convertNextDates(local.nextDates, 3);

      if (!newFormattedNextDates.length) {
        return reset();
      }

      setSplitedNextDates(newFormattedNextDates);
      setSplitedNextDatesPos(0);

      if (trySeedFromBatch(local, newFormattedNextDates)) {
        return;
      }

      const startHour = newFormattedNextDates[0].at(0);
      const endHour = newFormattedNextDates[0].at(-1);

      if (!startHour || !endHour) {
        return reset();
      }

      await getAgendas(startHour.split("T")[0], endHour.split("T")[0]);
    } catch (error) { }
  };

  const changePlace = async () => {
    setAgenda([]);
    const newLocal = places[placePosition];

    if (!newLocal) {
      return reset();
    }

    setSelectedLocal(newLocal);

    if (!newLocal.nextDates || !newLocal.nextDates.length) {
      return reset();
    }
    const newFormattedNextDates = convertNextDates(newLocal.nextDates, 3);

    if (!newFormattedNextDates.length) {
      return reset();
    }

    const startHour = newFormattedNextDates[0].at(0);
    const endHour = newFormattedNextDates[0].at(-1);

    if (!startHour || !endHour) {
      return reset();
    }

    await getAgendas(
      startHour.split("T")[0],
      endHour.split("T")[0],
      String(newLocal.id)
    );

    setSplitedNextDates(newFormattedNextDates);
    setSplitedNextDatesPos(0);
  };

  useEffect(() => {
    if (prevPlacePos.current === placePosition) return; // mount e re-run do StrictMode
    prevPlacePos.current = placePosition;
    changePlace();
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placePosition]);

  useEffect(() => {
    // aguarda a primeira tentativa de lote (sucesso ou falha) antes de decidir:
    // efeitos de filho rodam antes dos do pai, então sem este gate o card
    // dispararia o fetch individual antes de o lote sequer começar
    if (batchLoading || !batchAttempted) return;

    handleFirstData(selectedLocal);
    return () => {
      setAgenda([]);
      setSearchingAgenda(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchLoading, batchAttempted]);

  return (
    <>
      <div className="card mb-5 professional-card">
        <div className="row g-0">
          <div className={`col-12 ${!mapBig && 'col-md-6 col-xl-7'} px-4 py-4`}>
            <div className="row align-items-center g-3 mb-3">
              <div className="col-auto">
                <div className="doctor-avatar">
                  <Link
                    to={`/${partnerUrl}/profissional`}
                    state={doctor.id}
                    className={
                      pathname.includes("/profissional")
                        ? "pe-none"
                        : undefined
                    }
                  >
                    {/* //? 0 - Feminino, 1 - Masculino */}
                    <img
                      alt={`${gender === 0 ? "Dra." : "Dr."} ${name}`}
                      src={
                        image
                          ? image
                          : gender === 0
                            ? "https://conectadoc.com.br/ImgMedico/Thumbs/ThumbFemale.jpg"
                            : "https://conectadoc.com.br/ImgMedico/Thumbs/ThumbMale.jpg"
                      }
                      className="doctor-img"
                      loading="lazy"
                    />
                  </Link>
                  <Tooltip title="Profissional Verificado">
                    <span className="doctor-verified" aria-label="Profissional verificado">
                      <i className="las la-check" aria-hidden="true"></i>
                    </span>
                  </Tooltip>
                </div>
              </div>
              <div className="col">
                <h6 className="crm-label mb-1">{crm}</h6>
                <Link
                  to={`/${partnerUrl}/profissional`}
                  state={doctor.id}
                  className={`link-hover text-body ${pathname.includes("/profissional") && "pe-none"
                    }`}
                >
                  <h5 className="mb-1">
                    {gender === 0 ? "Dra." : "Dr."} {name}
                  </h5>
                </Link>
                {
                  <h6 className="mb-1 overflow-wrap text-muted fw-normal">
                    {doctor.specialty[0]?.name}
                  </h6>
                }
                {doctor.specialty[0]?.rqe &&
                  doctor.specialty[0]?.rqe !== "" && (
                    <p className="mb-0 small text-muted">RQE - {doctor.specialty[0]?.rqe}</p>
                  )}
              </div>
            </div>
            <div className="row">
              {doctor.specialty.length > 0 &&
                doctor.specialty[0].name.length > 0 && (
                  <div className={`col-12 ${!mapBig && 'col-sm-6'} pb-3`}>
                    <h6 className="mb-1 text-muted">Áreas de Atuação</h6>
                    <div className="text-format text-12 text-muted details-height">
                      <ul>
                        {doctor.specialty.map((elm, i) => (
                          <li key={i}>
                            <span className="text-default">{elm.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

              <div className={`col-12 ${!mapBig && 'col-sm-6'} pb-3`}>
                {places &&
                  selectedLocal &&
                  selectedLocal.healthInsurance[0] &&
                  selectedLocal.healthInsurance[0].name.length > 0 && (
                    <>
                      <h6 className="mb-1 text-muted">
                        Convênios{" "}
                        <Tooltip title="Convênios atendidos para consulta presencial">
                          <InfoOutlinedIcon
                            fontSize="small"
                            className="las la-info-circle"
                          />
                        </Tooltip>
                      </h6>
                      <div className="text-format text-12 text-muted details-height">
                        <ul>
                          {selectedLocal.healthInsurance.map((elm, i) => (
                            <li key={i}>
                              <span className="text-default">{elm.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
              </div>
            </div>
            {selectedLocal && (
              <p className="mt-3 mb-0 text-center small text-muted">
                <i className="las la-video text-primary me-2"></i>Teleconsulta
                somente particular.
              </p>
            )}
          </div>
          <div className={`col-12 ${!mapBig && 'col-md-6 col-xl-5'} bg-light hover border-md-left border-top border-md-top-0 px-4 py-4`}>
            <h6 className="text-center">
              <i className="las la-map-marker-alt text-primary me-2"></i>
              {places.length} Loca{places.length === 1 ? "l" : "is"} de atendimento
            </h6>
            <>
              {places.length > 0 && (
                <>
                  {places.length === 1 ? (
                    <div className="d-grid mb-4">
                      <div className="place-static text-start rounded-4 bg-white text-wrap px-3 py-2 border">
                        <div className="row g-0 align-items-center flex-nowrap w-100">
                          <div className="col">
                            <h6 className="mb-1">{places[0].name}</h6>
                            <p className="opacity-50 small mb-0">
                              <small>
                                {places[0].addressName}, {places[0].number}{" "}
                                <br /> CEP: {places[0].cep}{" "}
                                {places[0].neighborhood} - {places[0].city}/
                                {places[0].state}
                              </small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <PlacesCard
                      setSelected={setPlacePosition}
                      selected={placePosition}
                      locals={places}
                    />
                  )}
                </>
              )}
            </>

            <div className="agenda">
              {searchingAgenda ? (
                <>
                  <SchedulesSkeleton />
                </>
              ) : (
                <>
                  <h6 className="text-center">
                    {selectedLocal && (
                      <>
                        Agenda - <b>{selectedLocal?.name}</b>{" "}
                      </>
                    )}
                  </h6>
                  {agenda.length > 0 ? (
                    <>
                      <AgendaCard
                        start={startAgenda}
                        end={endAgenda}
                        nextDates={handleGetNextDates}
                        previousDates={handleGetPreviousDates}
                        agenda={agenda}
                        openModal={handleOpenAgendamentoModal}
                      />
                    </>
                  ) : (
                    <NoAgendaCard openModal={setRequestConsultModal} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {openAgendamentoModal && doctor && (
        <AgendamentoCard
          restriction={selectedLocal.restictionsObservations}
          modal={openAgendamentoModal}
          setModal={setOpenAgendamentoModal}
        />
      )}
      {requestConsultModal && (
        <ConsultationRequest
          modal={requestConsultModal}
          setModal={setRequestConsultModal}
          doctor={doctor}
        />
      )}
    </>
  );
};
